import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Alert,
  Skeleton,
  Tooltip,
  Input,
  Select,
  Row,
  Col,
} from 'antd';
import {
  MessageOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customerService from '../../services/customerService';
import PageHeader from '../../components/common/PageHeader';
import PageShell from '../../components/common/PageShell';
import { InvoiceStatusBadge, EstimateStatusBadge } from '../../components/common/StatusBadge';

dayjs.extend(relativeTime);

const { Text } = Typography;

const TYPE_OPTIONS = [
  { value: '',         label: 'All Types'  },
  { value: 'invoice',  label: 'Invoice'    },
  { value: 'estimate', label: 'Estimate'   },
];

function DocumentTypeBadge({ type }) {
  return (
    <Text>{type}</Text>
  );
}

const WorkspacesPage = () => {
  const navigate = useNavigate();

  const [data, setData]                         = useState([]);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [initialLoadComplete, setInitialLoad]   = useState(false);
  const [searchInput, setSearchInput]           = useState('');
  const [search, setSearch]                     = useState('');
  const [typeFilter, setTypeFilter]             = useState('');

  const searchDebounceRef = useRef(null);
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(val), 350);
  }, []);

  const filtersRef = useRef({ search, typeFilter });
  filtersRef.current = { search, typeFilter };

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const result = await customerService.listWorkspaceEvents(signal);
      setData(result);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      setError('Failed to load workspaces. Please try again.');
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setInitialLoad(true);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const filtered = useMemo(() => {
    const { search: s, typeFilter: t } = filtersRef.current;
    return data.filter((r) => {
      const matchesSearch = !s || r.number?.toLowerCase().includes(s.toLowerCase());
      const matchesType   = !t  || r.document_type === t;
      return matchesSearch && matchesType;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, typeFilter]);

  const openWorkspace = (record) => {
    navigate(`/workspaces/${record.document_type}/${record.id}`);
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      width: 110,
      render: (val) => <DocumentTypeBadge type={val} />,
    },
    {
      title: 'Reference',
      dataIndex: 'number',
      key: 'number',
      render: (val, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => openWorkspace(record)}
        >
          {val ?? '—'}
        </Button>
      ),
    },

    {
      title: 'Messages',
      dataIndex: 'workspace_event_count',
      key: 'workspace_event_count',
      width: 110,
      align: 'center',
      render: (val) => (
        <Space size={4}>
          <MessageOutlined style={{ color: 'var(--varexe-primary)', fontSize: 13 }} />
          <Text strong>{val ?? 0}</Text>
        </Space>
      ),
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_activity_at',
      key: 'last_activity_at',
      render: (val) => {
        if (!val) return <Text type="secondary">—</Text>;
        const d = dayjs(val);
        return (
          <Tooltip title={d.format('MMM D, YYYY h:mm A')}>
            <Text type="secondary" style={{ fontSize: 12 }}>{d.fromNow()}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val, record) =>
        record.document_type === 'invoice'
          ? <InvoiceStatusBadge status={val} />
          : <EstimateStatusBadge status={val} />,
    },
    {
      title: '',
      key: 'action',
      width: 80,
      align: 'right',
      render: (_, record) => (
        <Tooltip title="Open">
          <Button
            variant="filled"
            color="primary"
            size="small"
            icon={<ArrowRightOutlined />}
            onClick={() => openWorkspace(record)}
          />
        </Tooltip>
      ),
    },
  ];

  if (!initialLoadComplete) {
    return (
      <PageShell>
        <PageHeader
          title="Workspaces"
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Workspaces' }]}
        />
        <Card className="shadow-none" variant="borderless" styles={{ body: { padding: 20 } }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Workspaces"
        breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Workspaces' }]}
      />

      {error && (
        <Alert
          type="error"
          title={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16, borderRadius: 6 }}
          action={
            <Button size="small" variant="filled" onClick={() => load()}>
              Retry
            </Button>
          }
        />
      )}

      <Card
        className="shadow-none"
        variant="borderless"
        styles={{ body: { padding: 0 } }}
      >
        <Row
          gutter={[12, 12]}
          align="middle"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--varexe-border-color)',
            background: 'var(--varexe-white)',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Col xs={24} sm={10} md={8}>
            <Input
              prefix={<SearchOutlined style={{ color: 'var(--varexe-gray-400)' }} />}
              placeholder="Search by reference number…"
              value={searchInput}
              onChange={handleSearchChange}
              allowClear
              onClear={() => { setSearchInput(''); setSearch(''); }}
            />
          </Col>
          <Col xs={24} sm={7} md={5}>
            <Select
              style={{ width: '100%' }}
              value={typeFilter}
              onChange={setTypeFilter}
              options={TYPE_OPTIONS}
              suffixIcon={<FilterOutlined />}
            />
          </Col>
          <Col xs={24} sm={7} md={11} style={{ textAlign: 'right' }}>
            <Button
              variant="filled"
              color="default"
              icon={<ReloadOutlined />}
              onClick={() => load()}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Table
          className="listing-table"
          dataSource={filtered}
          columns={columns}
          rowKey={(r) => `${r.document_type}-${r.id}`}
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
          onRow={(record) => ({
            onClick: () => openWorkspace(record),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: 'No workspace activity found' }}
        />
      </Card>
    </PageShell>
  );
};

export default WorkspacesPage;
